import { useEffect, useState, useCallback } from "react";
import { Bell, Check, CheckCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const SEVERITY_COLOUR = {
  info: "bg-sky-500",
  success: "bg-emerald-500",
  warning: "bg-amber-500",
  error: "bg-rose-500",
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/notifications?limit=15");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) {
      // don't toast on the polling call — but log for observability
      if (e?.response?.status && e.response.status !== 401) {
        console.warn("[Notifications] load failed", e.response.status);
      }
    }
    finally { setBusy(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000); // refresh every minute
    return () => clearInterval(t);
  }, [load]);

  const markOne = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) {
      console.warn("[Notifications] markOne failed", e?.message || e);
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
    } catch (e) {
      console.warn("[Notifications] markAll failed", e?.message || e);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={TID.notifBell}
          className="relative w-9 h-9 rounded-md border border-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors"
          aria-label="Notifications"
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-gbe-accent text-gbe text-[10px] font-bold flex items-center justify-center px-1">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-gbe text-white">
          <div className="text-sm font-medium">Notifications</div>
          <button
            data-testid={TID.notifMarkAll}
            onClick={markAll}
            disabled={unread === 0}
            className="text-xs text-white/80 hover:text-white disabled:opacity-40 flex items-center gap-1"
          >
            <CheckCheck className="w-3 h-3" /> Mark all read
          </button>
        </div>

        {busy && (
          <div className="p-8 text-center text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
          </div>
        )}

        {!busy && items.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">You're all caught up.</div>
        )}

        <div className="max-h-96 overflow-y-auto">
          {items.map((n) => (
            <div
              key={n.id}
              data-testid={TID.notifItem(n.id)}
              className={`px-4 py-3 border-b border-slate-100 last:border-0 ${n.is_read ? "bg-white" : "bg-slate-50"}`}
            >
              <div className="flex items-start gap-2">
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${SEVERITY_COLOUR[n.severity] || "bg-slate-400"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium text-slate-900 truncate">{n.title}</div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">{n.module}</div>
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">{n.message}</div>
                  <div className="text-[11px] text-slate-400 mt-1">{n.created_at?.slice(0, 16).replace("T", " ")}</div>
                </div>
                {!n.is_read && (
                  <button onClick={() => markOne(n.id)} className="text-slate-400 hover:text-slate-700" title="Mark as read">
                    <Check className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
